import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ScheduleAuditDialog } from "../components/ScheduleAuditDialog";
import { ChevronLeft, ChevronRight, Plus, Repeat, AlarmClock } from "lucide-react";

const STATUS_CHIP = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const FREQ_DAYS = { weekly: 7 };
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addFreq(d, freq) {
  const n = new Date(d);
  if (freq === "weekly") n.setDate(n.getDate() + FREQ_DAYS.weekly);
  else if (freq === "monthly") n.setMonth(n.getMonth() + 1);
  else n.setMonth(n.getMonth() + 3);
  return n;
}

export default function Calendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSchedule = ["corporate_admin", "regional_head", "hotel_gm"].includes(user.role);
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [audits, setAudits] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultDate, setDefaultDate] = useState("");

  const load = () => {
    api.get("/audits").then((r) => setAudits(r.data));
    if (canSchedule) api.get("/schedules").then((r) => setSchedules(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable; run once on mount
  useEffect(() => { load(); }, []);

  const todayStr = ymd(new Date());
  const monthLabel = anchor.toLocaleString("default", { month: "long", year: "numeric" });

  const { weeks, events } = useMemo(() => {
    const start = new Date(anchor);
    start.setDate(1 - start.getDay());
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    end.setDate(end.getDate() + (6 - end.getDay()));
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) days.push(new Date(d));
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

    const events = {};
    const push = (dateStr, ev) => { (events[dateStr] = events[dateStr] || []).push(ev); };
    for (const a of audits) {
      const dateStr = (a.scheduled_date || "").slice(0, 10);
      if (!dateStr) continue;
      const overdue = a.status !== "completed" && (a.due_date || "").slice(0, 10) < todayStr;
      push(dateStr, { kind: "audit", id: a.id, label: a.template_name, hotel: a.hotel_name, status: a.status, overdue, auditor_id: a.auditor_id });
    }
    for (const s of schedules) {
      if (!s.active || !s.next_run) continue;
      let run = new Date(s.next_run.slice(0, 10) + "T12:00:00");
      for (let i = 0; i < 12 && run <= end; i++) {
        if (run >= start) push(ymd(run), { kind: "projected", id: s.id, label: s.template_name, hotel: s.hotel_name, freq: s.frequency });
        run = addFreq(run, s.frequency);
      }
    }
    return { weeks, events };
  }, [anchor, audits, schedules, todayStr]);

  const openEvent = (ev) => {
    if (ev.kind === "projected") { navigate("/audits"); return; }
    if (ev.status === "completed") navigate(`/audits/${ev.id}/report`);
    else if (ev.auditor_id === user.id || user.role === "corporate_admin") navigate(`/audits/${ev.id}/run`);
    else navigate("/audits");
  };

  const dayClick = (dateStr) => {
    if (!canSchedule) return;
    setDefaultDate(dateStr);
    setDialogOpen(true);
  };

  const shiftMonth = (n) => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + n, 1));

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Audit Calendar</h1>
          <p className="text-stone-500 mt-1 text-sm">Upcoming, overdue and recurring audits at a glance{canSchedule ? " — click any day to schedule." : "."}</p>
        </div>
        <div className="flex items-center gap-3">
          {canSchedule && (
            <Button data-testid="calendar-schedule-button" className="bg-[#14532D] hover:bg-[#166534]" onClick={() => { setDefaultDate(todayStr); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />Schedule Audit
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 lg:px-5 py-4 border-b border-[#E7E5DF]">
          <div className="flex items-center gap-2">
            <button data-testid="calendar-prev" className="p-2 rounded-lg hover:bg-stone-100" onClick={() => shiftMonth(-1)}><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="calendar-today" className="px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-300 hover:bg-stone-50" onClick={() => { const d = new Date(); d.setDate(1); setAnchor(d); }}>Today</button>
            <button data-testid="calendar-next" className="p-2 rounded-lg hover:bg-stone-100" onClick={() => shiftMonth(1)}><ChevronRight className="w-4 h-4" /></button>
          </div>
          <h2 data-testid="calendar-month-label" className="text-lg font-bold tracking-tight text-stone-900">{monthLabel}</h2>
          <div className="hidden md:flex items-center gap-4 text-[11px] text-stone-500">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" />Scheduled</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" />In progress</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Completed</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" />Overdue</span>
            <span className="flex items-center gap-1.5"><Repeat className="w-3 h-3" />Recurring (projected)</span>
          </div>
        </div>

        <div className="overflow-x-auto">
        <div className="min-w-[700px]">
        <div className="grid grid-cols-7 border-b border-[#E7E5DF] bg-stone-50">
          {DOW.map((d) => <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-stone-500">{d}</div>)}
        </div>

        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 border-b border-stone-100 last:border-0">
            {week.map((day) => {
              const dateStr = ymd(day);
              const inMonth = day.getMonth() === anchor.getMonth();
              const isToday = dateStr === todayStr;
              const evs = events[dateStr] || [];
              return (
                <div
                  key={dateStr}
                  data-testid={`calendar-day-${dateStr}`}
                  onClick={() => dayClick(dateStr)}
                  className={`min-h-[108px] border-r border-stone-100 last:border-r-0 p-1.5 align-top transition-colors ${inMonth ? "bg-white" : "bg-stone-50/60"} ${canSchedule ? "cursor-pointer hover:bg-emerald-50/40" : ""}`}
                >
                  <div className="flex justify-end">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-mono ${isToday ? "bg-[#14532D] text-white font-bold" : inMonth ? "text-stone-700" : "text-stone-400"}`}>{day.getDate()}</span>
                  </div>
                  <div className="space-y-1 mt-0.5">
                    {evs.slice(0, 3).map((ev, i) => (
                      <button
                        key={i}
                        data-testid={`calendar-event-${ev.id}`}
                        onClick={(e) => { e.stopPropagation(); openEvent(ev); }}
                        title={`${ev.label} — ${ev.hotel}`}
                        className={`w-full text-left px-1.5 py-1 rounded border text-[10px] leading-tight font-medium truncate flex items-center gap-1 ${
                          ev.kind === "projected"
                            ? "border-dashed border-stone-300 text-stone-500 bg-stone-50"
                            : ev.overdue
                            ? "bg-rose-50 text-rose-700 border-rose-300"
                            : STATUS_CHIP[ev.status]
                        }`}
                      >
                        {ev.kind === "projected" ? <Repeat className="w-2.5 h-2.5 shrink-0" /> : ev.overdue ? <AlarmClock className="w-2.5 h-2.5 shrink-0" /> : null}
                        <span className="truncate">{ev.label}</span>
                      </button>
                    ))}
                    {evs.length > 3 && <p className="text-[10px] text-stone-400 pl-1">+{evs.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        </div>
        </div>
      </div>

      <ScheduleAuditDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={load} defaultDate={defaultDate} />
    </div>
  );
}
